import cx from 'classnames';
import React, { useMemo, useState, forwardRef } from 'react';
import { elapsed } from '~/utils/misc';
import Card from '~/App/Components/Card';
import { Recording, RecordingType } from '~/types/models';
import { useRelativeDate } from '~/react-hooks/use-date';
import { Watched, Clicked } from '~/App/Modules/Common/Components/Icons';
import HoverImage from '~/App/Modules/MyLibrary/Components/HoverImage';
import AssetDetails from '~/App/Modules/MyLibrary/Components/AssetDetails';
import RecordingActionLinks from '~/App/Modules/MyLibrary/Components/RecordingActionLinks';
import AssetContact from '~/App/Modules/MyLibrary/Components/AssetContact';
import ResourceTagBar from '~/App/Modules/Tags/Components/ResourceTagBar';
import { TaggableResourceType } from '~/types/models/tags';
import { useTaggableResourcePage } from '../hooks/use-taggable-resource-page';
import { useThumbnailWithFallback } from '../hooks/use-thumbnail-with-fallback';

export type RecordingListItemProps = {
  recording: Recording;
  onTagUpdated: () => void;
  onThumbnailLoad?: () => void;
  cardClassName?: string;
  imageClassName?: string;
  hideTagBar?: boolean;
  hideContactTagging?: boolean;
  hideInteractionStats?: boolean;
  showDownloadLink?: boolean;
};

const RecordingListItem = forwardRef<HTMLDivElement, RecordingListItemProps>(
  (props, detailsContainerRef) => {
    const {
      recording,
      onTagUpdated,
      onThumbnailLoad,
      cardClassName,
      imageClassName,
      hideTagBar = false,
      hideContactTagging = false,
      hideInteractionStats = false,
      showDownloadLink = false,
    } = props;

    const taggableResourcePage = useTaggableResourcePage();
    const createdAgo = useRelativeDate(recording.created_at);
    const [showManageContactModal, setShowManageContactModal] = useState(false);
    const { thumbnail, onThumbnailLoadError, isUsingFallbackThumbnail } =
      useThumbnailWithFallback({
        imageUrl: recording.media.thumbnail_source,
      });

    const editUrl = useMemo(
      () => `/recordings/${recording.type}/${recording.id}/edit`,
      [recording.id]
    );

    const { views, clicks } = useMemo(
      () => recording.interactions || {},
      [recording]
    );

    const videoDuration =
      recording.duration && elapsed(recording.duration, { noPadMinutes: true });

    const hasContact = recording.author !== null;

    const hideStat = hasContact || hideInteractionStats;

    const openManageContactModal = () => setShowManageContactModal(true);

    return (
      <>
        <HoverImage>
          <Card
            className={cx(cardClassName, [
              '!p-0 overflow-hidden w-full relative h-[8.5rem]',
            ])}
          >
            <a href={editUrl}>
              <img
                className={cx(
                  imageClassName,
                  'object-contain w-full h-[151px]',
                  RecordingType.Webcam === recording.type &&
                    !isUsingFallbackThumbnail
                    ? 'object-none'
                    : 'object-cover'
                )}
                src={thumbnail}
                onLoad={onThumbnailLoad}
                onError={onThumbnailLoadError}
              />
            </a>
            {!!recording.duration && (
              <span className='absolute bottom-2.5 right-2.5 text-base text-white bg-black/50 px-2 py-1 rounded-md'>
                {videoDuration}
              </span>
            )}
          </Card>
        </HoverImage>
        <div ref={detailsContainerRef}>
          <AssetDetails
            name={recording.name}
            redirectUrl={editUrl}
            actionLinksComponent={
              <RecordingActionLinks
                recording={recording}
                openManageContactModal={showManageContactModal}
                onRequestOpenManageContactModal={openManageContactModal}
                onRequestCloseManageContactModal={() =>
                  setShowManageContactModal(false)
                }
                hideContactTagging={hideContactTagging}
                showDownloadLink={showDownloadLink}
              />
            }
          >
            {hasContact && (
              <AssetContact
                name={recording.author?.name as string}
                onClick={openManageContactModal}
              />
            )}
            {!hideStat && (
              <>
                <Watched /> <span className='ml-1.5'>{views ?? 0}</span>
              </>
            )}
            {!hideStat && !!clicks && <Clicked />}
            {createdAgo}
          </AssetDetails>
          {!hideTagBar && (
            <div>
              <ResourceTagBar
                emptyText='Add a tag'
                onTagResourceSuccess={onTagUpdated}
                onUnTagResourceSuccess={onTagUpdated}
                resource={recording}
                resourceType={TaggableResourceType.UserAsset}
                sourcePage={taggableResourcePage}
              />
            </div>
          )}
        </div>
      </>
    );
  }
);

export default RecordingListItem;
